import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import classNames from "classnames/bind";
import debounce from "lodash/debounce";

import { ColourOption } from "@/components/Colours/types.ts";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { PlusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { MinusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { ChevronLeftOutlined as ChevronLeft } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronLeftOutlined";
import { ChevronRightOutlined as ChevronRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import { Swatch } from "@/components/Colours/ColourList/Swatches/Swatch/Swatch.tsx";

import styles from "./Swatches.module.scss";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK } from "@/components/Colours/ColourList/monetateEvents.ts";

const cx = classNames.bind(styles);

export const SWATCH_ITEM_SIZE = 44;
export const GAP_SIZE = 12;
export const INNER_WRAPPER_PADDING = 12;
export const FADE_AREA_WIDTH = 10;

type Props = {
  /**
   * An array of colour option objects.
   */
  colourOptions: ColourOption[];
  /**
   * A boolean flag changing the colour selector behaviour for the dior brand
   */
  isDior: boolean;
  /**
   * A number representing the index of selected colour
   */
  selectedColourIndex: number;
  /**
   * A boolean toggling history push with replace
   */
  replaceHistoryEntryOnVariantChange: boolean;
  /**
   * A boolean indicating if the product is in the beauty category
   */
  isBeautyCategory: boolean;
};

export const Swatches = ({
  isDior,
  colourOptions,
  selectedColourIndex,
  replaceHistoryEntryOnVariantChange,
  isBeautyCategory,
}: Props) => {
  const isLocalChange = useRef(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [visibleItemCount, setVisibleItemCount] = useState<
    number | undefined
  >();
  const [hideToggleButton, setHideToggleButton] = useState<boolean>(true);
  const coloursContainerRef = useRef<HTMLUListElement | null>(null);
  const activeElementRef = useRef<HTMLLIElement | null>(null);
  const skipLinkTargetRef = useRef<HTMLParagraphElement | null>(null);
  const [skipLinkTargetVisible, setSkipLinkTargetVisible] =
    useState<boolean>(false);
  const totalItems = colourOptions.length;
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const { ref: firstItemRef, inView: firstItemInView } = useInView({
    root: wrapperRef.current,
    threshold: 1,
    initialInView: true,
  });
  const { ref: lastItemRef, inView: lastItemInView } = useInView({
    root: wrapperRef.current,
    threshold: 1,
    initialInView: true,
  });

  const scrollToActiveItem = (
    container: HTMLElement,
    activeElement: HTMLElement,
  ) => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = activeElement.getBoundingClientRect();

    const containerWidth = container.clientWidth;
    const elementWidth = elementRect.width;

    const elementCenter = elementRect.left + elementWidth / 2;
    const containerCenter = containerRect.left + containerWidth / 2;

    let scrollOffset = elementCenter - containerCenter;
    const currentScrollLeft = container.scrollLeft;

    const leftOffset =
      selectedColourIndex >= 1 ? GAP_SIZE + elementWidth * 0.25 : 0;
    const rightOffset =
      selectedColourIndex < colourOptions.length - 1
        ? GAP_SIZE + elementWidth * 0.25
        : 0;

    const prevElementOffset = GAP_SIZE + FADE_AREA_WIDTH + elementWidth * 0.25;
    const nextElementOffset = GAP_SIZE + FADE_AREA_WIDTH + elementWidth * 0.25;

    const leftEdgeVisible =
      elementRect.left >= containerRect.left + leftOffset &&
      elementRect.left <= containerRect.right - rightOffset;
    const rightEdgeVisible =
      elementRect.right >= containerRect.left + leftOffset &&
      elementRect.right <= containerRect.right - rightOffset;

    if (!leftEdgeVisible && !rightEdgeVisible) {
      container.scrollTo({
        left: currentScrollLeft + scrollOffset,
        behavior: "instant",
      });
    }

    if (
      (!leftEdgeVisible && rightEdgeVisible) ||
      (leftEdgeVisible && !rightEdgeVisible)
    ) {
      if (!leftEdgeVisible) {
        scrollOffset =
          elementRect.left - containerRect.left - prevElementOffset;
      } else if (!rightEdgeVisible) {
        scrollOffset =
          elementRect.right - containerRect.right + nextElementOffset;
      }

      container.scrollTo({
        left: currentScrollLeft + scrollOffset,
        behavior: "instant",
      });
    }
  };

  const scrollToActiveItemWithOffset = (
    container: HTMLElement,
    activeElement: HTMLElement,
  ) => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = activeElement.getBoundingClientRect();
    const elementWidth = elementRect.width;

    const prevElementOffset = GAP_SIZE + FADE_AREA_WIDTH + elementWidth * 0.25;
    const nextElementOffset = GAP_SIZE + FADE_AREA_WIDTH + elementWidth * 0.25;

    const leftEdgeVisible =
      elementRect.left >= containerRect.left + prevElementOffset;
    const rightEdgeVisible =
      elementRect.right <= containerRect.right - nextElementOffset;

    let scrollOffset = 0;

    if (!leftEdgeVisible) {
      scrollOffset = elementRect.left - containerRect.left - prevElementOffset;
    } else if (!rightEdgeVisible) {
      scrollOffset =
        elementRect.right - containerRect.right + nextElementOffset;
    }

    container.scrollTo({
      left: container.scrollLeft + scrollOffset,
      behavior: "smooth",
    });
  };

  const handleColourSelectClick = () => {
    isLocalChange.current = true;
  };

  const recalculateRows = () => {
    if (!coloursContainerRef.current) return;

    const rect = coloursContainerRef.current.getBoundingClientRect();
    const containerWidth = rect.width;
    const itemWidth = SWATCH_ITEM_SIZE + GAP_SIZE;
    const columns = Math.floor(
      (containerWidth + GAP_SIZE - INNER_WRAPPER_PADDING) / itemWidth,
    );

    const maxRows = Math.ceil(totalItems / columns);

    const requiredRow = Math.floor(selectedColourIndex / columns) + 1;
    const rows = Math.max(3, requiredRow);

    setHideToggleButton(rows === maxRows || maxRows < 3);

    setVisibleItemCount(Math.min(rows * columns, totalItems));
  };

  const handleResize = debounce(() => {
    recalculateRows();
  }, 200);

  useEffect(() => {
    const scrollFunction = isLocalChange.current
      ? scrollToActiveItemWithOffset
      : scrollToActiveItem;

    isLocalChange.current = false;

    if (!coloursContainerRef.current || !activeElementRef.current) {
      return;
    }

    scrollFunction(coloursContainerRef.current, activeElementRef.current);
  }, [selectedColourIndex]);

  useEffect(() => {
    recalculateRows();
  }, [selectedColourIndex]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    setPrevButtonDisabled(firstItemInView);
    setNextButtonDisabled(lastItemInView);
  }, [firstItemInView, lastItemInView]);

  const toggleShowMore = () => {
    setIsExpanded((prev) => !prev);
    if (visibleItemCount) {
      coloursContainerRef.current
        ?.querySelectorAll("a")
        [visibleItemCount - 1]?.focus(); // Set focus on the last visible item
    }
    dispatchMonetateQEvent(COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK);
  };

  const handleSkipLink = () => {
    setSkipLinkTargetVisible(true);
    setTimeout(() => {
      skipLinkTargetRef.current?.focus();
    }, 0);
  };

  const handleButtonNextClick = () => {
    if (
      typeof coloursContainerRef === "function" ||
      !coloursContainerRef ||
      !coloursContainerRef.current
    ) {
      return;
    }

    const distance = coloursContainerRef.current.clientWidth / 2;
    const scrollLeft = coloursContainerRef.current.scrollLeft;

    coloursContainerRef.current.scrollTo({
      left: scrollLeft + distance,
      behavior: "smooth",
    });
  };

  const handleButtonPrevClick = () => {
    if (
      typeof coloursContainerRef === "function" ||
      !coloursContainerRef ||
      !coloursContainerRef.current
    ) {
      return;
    }

    const distance = coloursContainerRef.current.clientWidth / 2;
    const scrollLeft = coloursContainerRef.current.scrollLeft;

    coloursContainerRef.current.scrollTo({
      left: scrollLeft - distance,
      behavior: "smooth",
    });
  };

  const getRef = (ind: number) => {
    if (ind === 0) {
      return firstItemRef;
    }
    if (ind === colourOptions.length - 1) {
      return lastItemRef;
    }
    return null;
  };

  return (
    <section className={styles.swatchSection} ref={wrapperRef}>
      <h5 className={styles.visuallyHidden}>Colour swatches</h5>
      <p className={styles.visuallyHidden}>
        In this section there is a list of {totalItems} colour swatches, which
        are an alternative way to explore the colour range. If you wish to skip
        the swatches and go to the end of the list, please use the following
        link.
      </p>
      <a
        className={styles.skipLink}
        href="#colour-list-end"
        onClick={handleSkipLink}
      >
        Skip the colour swatches
      </a>
      <ul
        className={cx(styles.list, { beautyCategory: isBeautyCategory })}
        ref={coloursContainerRef}
        role="listbox"
        aria-label="Colour selection swatches"
      >
        {colourOptions.map((colourOption, idx) => (
          <div ref={getRef(idx)} key={colourOption.colour}>
            <li
              ref={idx === selectedColourIndex ? activeElementRef : undefined}
              key={colourOption.colour}
              className={cx("item", {
                hidden:
                  visibleItemCount && !isExpanded
                    ? idx >= visibleItemCount
                    : false,
              })}
              onClick={handleColourSelectClick}
            >
              <Swatch
                colour={colourOption}
                isDior={isDior}
                colourIndex={idx}
                replaceHistoryEntryOnVariantChange={
                  replaceHistoryEntryOnVariantChange
                }
              />
            </li>
          </div>
        ))}
      </ul>
      <button
        data-testid="colour:swatches:scroll:button:previous"
        className={cx(styles.buttonPrev, {
          disabled: prevButtonDisabled,
          beautyCategory: isBeautyCategory,
        })}
        onClick={handleButtonPrevClick}
        aria-label="Scroll colour swatches left"
      >
        <ChevronLeft size="medium" />
      </button>
      <button
        data-testid="colour:swatches:scroll:button:next"
        className={cx(styles.buttonNext, {
          disabled: nextButtonDisabled,
          beautyCategory: isBeautyCategory,
        })}
        onClick={handleButtonNextClick}
        aria-label="Scroll colour swatches right"
      >
        <ChevronRight size="medium" />
      </button>
      {!hideToggleButton && visibleItemCount !== undefined && (
        <TertiaryButton
          onClick={toggleShowMore}
          icon={isExpanded ? MinusOutlined : PlusOutlined}
          className={cx("toggleButton")}
          aria-expanded={isExpanded}
          aria-controls="colour-list-grid"
          labelText={
            isExpanded
              ? "Show less"
              : `Show ${colourOptions.length - visibleItemCount} more`
          }
        />
      )}
      {skipLinkTargetVisible && (
        <p
          className={styles.visuallyHidden}
          ref={skipLinkTargetRef}
          tabIndex={-1}
          onBlur={() => setSkipLinkTargetVisible(false)}
          id="colour-list-end"
        >
          End of secondary colour menu
        </p>
      )}
    </section>
  );
};
